// src/theme/mihuTheme.js
// MihuThemeProvider — injects the token CSS variables at runtime and exposes
// the token object + helper hooks to the React tree.
//
// Usage in your app root (e.g. src/App.jsx or src/main.jsx):
//   import { MihuThemeProvider } from '@/theme/mihuTheme';
//   <MihuThemeProvider>
//     <App />
//   </MihuThemeProvider>
//
// Then style any element with the variables via Tailwind arbitrary values:
//   <div className="bg-[var(--mihu-offwhite)] text-[var(--mihu-charcoal)]" />
//
// No changes to tailwind.config.js are required.

import React, { createContext, useContext, useEffect, useMemo } from "react";
import { tokens, cssVarMap } from "./tokens";

const MihuThemeContext = createContext(tokens);

export function MihuThemeProvider({ children, themeOverrides }) {
  // Merge base tokens with any overrides passed by the app.
  const resolvedTokens = useMemo(() => {
    if (!themeOverrides) return tokens;
    return {
      ...tokens,
      colors: { ...tokens.colors, ...themeOverrides.colors },
      fonts: { ...tokens.fonts, ...themeOverrides.fonts },
      ...themeOverrides,
    };
  }, [themeOverrides]);

  // Inject CSS variables onto :root once.
  useEffect(() => {
    const root = document.documentElement;
    Object.entries(cssVarMap).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });
    // Apply base body styling
    root.style.setProperty("color-scheme", "light");
  }, []);

  // Load Google Fonts once (idempotent)
  useEffect(() => {
    const id = "mihu-google-fonts";
    if (document.getElementById(id)) return;
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400&family=Montserrat:wght@400;500;600&family=Raleway:wght@400;500&display=swap";
    document.head.appendChild(link);
  }, []);

  return (
    <MihuThemeContext.Provider value={resolvedTokens}>
      {children}
    </MihuThemeContext.Provider>
  );
}

// Hook to access the raw token object (e.g. tokens.colors.navy).
export function useMihuTheme() {
  return useContext(MihuThemeContext);
}

// Convenience: get a single CSS variable reference for inline styles.
//   const navy = useToken('navy');  // -> 'var(--mihu-navy)'
export function useToken(name) {
  const t = useContext(MihuThemeContext);
  return `var(--mihu-${name})`;
}

export default MihuThemeProvider;